import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {Box, Button, Loading, Typography} from '@viptech/react-components'

import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import editPenBlue from 'src/images/editPenBlue.svg'
import NoImage from 'src/images/noImage.svg'
import {CameraImageDraw} from '../../../../../../../../common/components/camera-image-draw/CameraImageDraw'
import {Pos} from '../../../../../../../../common/types/generics/ImageDetections'
import {SpeedDetectionCoordinateDTO} from '../../../../../../../../services/api/endpoints/ObjectEndpoint'
import {useCameraConfigContext} from '../../../../../../context/CameraConfigContext'
import {StepperModalActionsButtonContainer, StepperModalBarrierContainer} from '../../../styles'
import Card from '../../common/components/Card'

import {CoordinatesType} from 'src/common/components/draw-lines-only/DrawBarrierWithSvg'
import useImage from '../../../../../../../../hooks/useImage'
import api from '../../../../../../../../services/api/api'
import {ChosenObject} from '../../StepperModal'

type ChooseBarrierStepProps = {
  nextFunction: (index: number, resume: string, advanceTo?: number) => void
  returnFunction: (index: number) => void
  setChosen: (choices: Partial<ChosenObject>) => void
  chosen: ChosenObject | null
}

const DrawAreaStep = (props: ChooseBarrierStepProps) => {
  const {nextFunction, returnFunction, setChosen} = props
  const {camera, _dimImage, setDimImage} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()

  const [points, setPoints] = useState<Pos[]>([
    {x: 200, y: 200},
    {x: 400, y: 200},
  ])

  const [save, setSave] = useState('')
  const [loadingSnapshot, setLoadingSnapshot] = useState(false)
  const [snapshot, setSnapshot] = useState<null | string>()
  const [linesCreated, setLinesCreated] = useState(1)
  const [line1, setLine1] = useState<SpeedDetectionCoordinateDTO[]>([])
  const [line2, setLine2] = useState<SpeedDetectionCoordinateDTO[]>([])

  const {width, height} = useImage(`data:image/jpeg;base64,${snapshot}`)

  useEffect(() => {
    setDimImage({height, width})
  }, [width, height])

  const [pointsToDrawWithSvg, setPointsToDrawWithSvg] = useState<CoordinatesType[]>([])

  useEffect(() => {
    if (camera?.id) handleGetSnapshot(camera.id)
  }, [])

  const handleGetSnapshot = async (cameraId: number) => {
    try {
      setLoadingSnapshot(true)
      const response = await api.camera.getSnapshot(cameraId)
      setSnapshot(response.data.data.time.base64)
    } catch (error) {
      enqueueSnackbar('Não é possível prosseguir sem a imagem da sua câmera', {variant: 'warning'})
    } finally {
      setLoadingSnapshot(false)
    }
  }

  const handleNextFunction = () => {
    const coordinates = [...line1, ...line2]
    if (snapshot) setChosen({coordinates, cameraId: camera?.id, type: 'speed'})
    nextFunction(2, 'Área desenhada')
  }

  const saveNewPoints = (points: Pos[]) => {
    if (linesCreated > 2) return
    const coordinates: SpeedDetectionCoordinateDTO[] = points.map((it) => {
      return {
        x: Math.round(it.x) / _dimImage.width,
        y: Math.round(it.y) / _dimImage.height,
        reference: linesCreated,
      }
    })
    if (linesCreated === 1) {
      setLine1(coordinates)
      setLinesCreated(2)
      setSelectedLine('Linha 1')
      setPointsToDrawWithSvg(
        points.map((it) => ({
          x: Math.round(it.x) / _dimImage.width,
          y: Math.round(it.y) / _dimImage.height,
          reference: '1',
        })),
      )
    } else {
      setLinesCreated(3)
      setLine2(coordinates)
      setSelectedLine('Linha 2')
      setShowToolbarKonva(false)
      setPoints(
        points.map((it) => ({
          x: Math.round(it.x),
          y: Math.round(it.y),
          reference: 2,
        })),
      )
    }
    setSave('')
  }

  function LabelText({label, text}: {label: string; text: string | number}) {
    return (
      <Box>
        <Typography variant='span' size='16px' style={{fontWeight: 500}}>
          {label}
        </Typography>
        {}
        <Typography variant='p' size='12px' color='#8E8E8E'>
          {text}
        </Typography>
      </Box>
    )
  }
  const [showToolbarKonva, setShowToolbarKonva] = useState(true)
  const [selectedLine, setSelectedLine] = useState('')

  const deleteLine1 = () => {
    setPointsToDrawWithSvg([])
    setLine1([])
    setLinesCreated(1)
    setShowToolbarKonva(true)
  }

  const deleteLine2 = () => {
    setPointsToDrawWithSvg([])
    setLine2([])
    setLinesCreated(2)
    setShowToolbarKonva(true)
  }

  return (
    <Box paddingTop='10px' display='flex' direction='column' justify='space-between'>
      <StepperModalBarrierContainer>
        <Box display='flex' direction='column' rowGap='24px' width='100%' padding='20px'>
          <LabelText
            label='Crie duas linhas paralelas próximas'
            text='Desenhe duas linhas com uma distância conhecida entre elas. As linhas devem estar próximas, mas não sobrepostas.'
          />
          {loadingSnapshot && (
            <Box display='flex' paddingTop='20px' justifyContent='center' align='center'>
              <Loading size='50px' />
            </Box>
          )}
          {!loadingSnapshot && snapshot && (
            <Box height='100%' width='100%' display='flex' align='center' justify='space-around'>
              <CameraImageDraw
                isKonvaImageDrawToolbar={showToolbarKonva}
                width={500}
                points={points}
                editable={showToolbarKonva}
                src={`data:image/jpeg;base64,${snapshot}`}
                height={320}
                onSave={saveNewPoints}
                save={save}
                maxAmountOfPoints={2}
                auxiliaryClick={() => setSave('save')}
                disableAuxiliaryClick={linesCreated > 2}
                pointsToDrawWithSvg={pointsToDrawWithSvg}
              />

              <Box display='flex' direction='column' rowGap='30px'>
                {line1.length > 0 && (
                  <Box display='flex' width='100%' justify='center' align='center'>
                    <Card
                      initialState={!selectedLine}
                      onClick={() => {
                        setPoints([])
                        setSelectedLine('Linha 1')
                        setPointsToDrawWithSvg(line1.map((it) => ({x: it.x, y: it.y, reference: '1'})))
                      }}
                      dispatchTo={'Linha 1'}
                      width='250px'
                      selected={selectedLine === 'Linha 1'}
                    />
                    <Box marginLeft='19px' display='flex'>
                      <Button
                        variant='outlined'
                        onClick={() => {
                          setPointsToDrawWithSvg([])
                          setPoints(line1.map((it) => ({x: it.x * _dimImage.width, y: it.y * _dimImage.height})))
                          setShowToolbarKonva(true)
                          setLinesCreated(1)
                        }}
                        color='none'
                        hoverColor='none'>
                        <img src={editPenBlue} alt='Menu logo' style={{width: 16, height: 16}} />
                      </Button>

                      <Button variant='outlined' onClick={() => deleteLine1()} color='transparent' hoverColor='none'>
                        <DeleteOutlineIcon style={{width: '21px', color: 'red'}} />
                      </Button>
                    </Box>
                  </Box>
                )}

                {line2.length > 0 && (
                  <Box display='flex' width='100%' justify='center' align='center'>
                    <Card
                      initialState={!selectedLine}
                      onClick={() => {
                        setPoints([])
                        setPointsToDrawWithSvg(line2.map((it) => ({x: it.x, y: it.y, reference: '2'})))
                        setSelectedLine('Linha 2')
                      }}
                      dispatchTo={`Linha 2`}
                      width='250px'
                      selected={selectedLine === 'Linha 2'}
                    />
                    <Box marginLeft='19px' display='flex'>
                      <Button
                        variant='outlined'
                        onClick={() => {
                          setPointsToDrawWithSvg([])
                          setPoints(line2.map((it) => ({x: it.x * _dimImage.width, y: it.y * _dimImage.height})))
                          setShowToolbarKonva(true)
                          setLinesCreated(2)
                        }}
                        color='transparent'
                        hoverColor='none'>
                        <img src={editPenBlue} alt='Menu logo' style={{width: 16, height: 16}} />
                      </Button>
                      <Button variant='outlined' onClick={() => deleteLine2()} color='transparent' hoverColor='none'>
                        <DeleteOutlineIcon style={{width: '21px', color: 'red'}} />
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {!loadingSnapshot && !snapshot && (
            <Box display='flex' width='350px' height='250px' margin='auto'>
              <img src={NoImage} alt='Sem imagem da câmera' />
            </Box>
          )}
        </Box>
      </StepperModalBarrierContainer>

      <StepperModalActionsButtonContainer style={{paddingTop: '64px'}}>
        <Button fontSize='12px' height='40px' width='150px' color='#8E8E8E' variant='outlined' onClick={() => returnFunction(0)}>
          Voltar
        </Button>
        <Button
          fontSize='12px'
          height='40px'
          disabledTextColor='#8E8E8E'
          width='150px'
          disabled={!snapshot?.length || !line1.length || !line2.length}
          onClick={() => handleNextFunction()}>
          Próximo
        </Button>
      </StepperModalActionsButtonContainer>
    </Box>
  )
}

export default DrawAreaStep
